<template>
  <div class="dashboard-driver-consumption-breakdown">
    <div class="breakdown-grid">
      <!-- kWh Pie -->
      <div class="breakdown-card">
        <h3 class="breakdown-title">
          <v-icon small color="#3B82F6">mdi-flash</v-icon>
          {{ $t('org-dashboard.driver-breakdown-kwh') }}
        </h3>
        <div class="breakdown-body">
          <div class="donut-wrap">
            <highcharts :options="kwhChartOptions" />
            <div class="donut-center">
              <span class="donut-value">{{ formatNumber(totalKwh) }}</span>
              <span class="donut-unit">kWh</span>
            </div>
          </div>
          <div class="breakdown-legend">
            <div class="legend-row" v-for="(seg, i) in segments" :key="'k'+i">
              <span class="legend-dot" :style="{ backgroundColor: segmentColor(seg.name) }"></span>
              <span class="legend-label">{{ segmentLabel(seg.name) }}</span>
              <span class="legend-value">{{ formatNumber(seg.kwh) }} kWh</span>
              <span class="legend-pct">{{ kwhPercent(seg) }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Cost Pie -->
      <div class="breakdown-card">
        <h3 class="breakdown-title">
          <v-icon small color="#F59E0B">mdi-currency-eur</v-icon>
          {{ $t('org-dashboard.driver-breakdown-cost') }}
        </h3>
        <div class="breakdown-body">
          <div class="donut-wrap">
            <highcharts :options="costChartOptions" />
            <div class="donut-center">
              <span class="donut-value">{{ formatNumber(totalCost) }}</span>
              <span class="donut-unit">€ HT</span>
            </div>
          </div>
          <div class="breakdown-legend">
            <div class="legend-row" v-for="(seg, i) in segments" :key="'c'+i">
              <span class="legend-dot" :style="{ backgroundColor: segmentColor(seg.name) }"></span>
              <span class="legend-label">{{ segmentLabel(seg.name) }}</span>
              <span class="legend-value">{{ formatNumber(seg.cost) }} €</span>
              <span class="legend-pct">{{ costPercent(seg) }}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

const SEGMENT_COLORS = {
  Home: '#10B981',
  Enterprise: '#3B82F6',
  RoamingOutgoing: '#F59E0B',
}

export default {
  name: 'DashboardDriverConsumptionBreakdown',
  components: {
    highcharts: Chart,
  },
  props: {
    segments: { type: Array, default: () => [] },
    totalKwh: { type: Number, default: 0 },
    totalCost: { type: Number, default: 0 },
  },

  computed: {
    kwhChartOptions() {
      return this.buildPieOptions(this.segments.map(s => ({
        name: this.segmentLabel(s.name),
        y: s.kwh || 0,
        color: this.segmentColor(s.name),
      })))
    },

    costChartOptions() {
      return this.buildPieOptions(this.segments.map(s => ({
        name: this.segmentLabel(s.name),
        y: s.cost || 0,
        color: this.segmentColor(s.name),
      })))
    },
  },

  methods: {
    segmentColor(name) {
      return SEGMENT_COLORS[name] || '#6B7280'
    },

    segmentLabel(name) {
      const labels = {
        Home: this.$t('org-dashboard.segment-home'),
        Enterprise: this.$t('org-dashboard.segment-enterprise'),
        RoamingOutgoing: this.$t('org-dashboard.segment-roaming'),
      }
      return labels[name] || name
    },

    kwhPercent(seg) {
      if (!this.totalKwh) return 0
      return ((seg.kwh / this.totalKwh) * 100).toFixed(1)
    },

    costPercent(seg) {
      if (!this.totalCost) return 0
      return ((seg.cost / this.totalCost) * 100).toFixed(1)
    },

    formatNumber(val) {
      if (val == null) return '-'
      return val.toLocaleString(undefined, { maximumFractionDigits: 1 })
    },

    buildPieOptions(data) {
      const hasData = data.some(d => d.y > 0)
      const pieData = hasData
        ? data
        : [{ name: this.$t('org-dashboard.no-data'), y: 1, color: '#e5e7eb' }]

      return {
        chart: {
          type: 'pie',
          height: 180,
          width: 180,
          backgroundColor: 'transparent',
          margin: [0, 0, 0, 0],
        },
        title: { text: '' },
        credits: { enabled: false },
        tooltip: {
          enabled: hasData,
          pointFormat: '<b>{point.y:.1f}</b> ({point.percentage:.1f}%)',
        },
        plotOptions: {
          pie: {
            innerSize: '68%',
            dataLabels: { enabled: false },
            borderWidth: 0,
            states: { hover: { brightness: hasData ? -0.05 : 0 } },
          },
        },
        series: [{ data: pieData }],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.breakdown-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.breakdown-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.breakdown-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 16px 0;
}

.breakdown-body {
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
}

.donut-wrap {
  position: relative;
  flex-shrink: 0;
  width: 180px;
  height: 180px;
}

.donut-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
}

.donut-value {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.donut-unit {
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
}

.breakdown-legend {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.legend-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-label {
  flex: 1;
  color: #374151;
  font-weight: 500;
}

.legend-value {
  color: #111827;
  font-weight: 600;
  white-space: nowrap;
}

.legend-pct {
  color: #9ca3af;
  font-size: 11px;
  min-width: 40px;
  text-align: right;
}
</style>
